import { MakeCallsFrom, PhoneNumberE164, TelecomPhoneLineId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { InlineBanner } from "components/ui/inline-banner";
import { InternalLink } from "components/ui/internal-link";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Spinner } from "components/ui/spinner";
import React, { useCallback } from "react";
import { handleEvent } from "util/user-events";

export const phoneSettingsPath = "/settings/account/phone";

export const callUsingPhoneHint =
    "Your phone rings first, then connects you to the contact.";

export const CallFromSaveFailure: React.FC = () => (
    <InlineBanner type="error">Unable to save your choice.</InlineBanner>
);

export type MyPhone =
    | { kind: "callable"; phoneNumber: PhoneNumberE164 }
    | { kind: "missing" }
    | { kind: "unusable" };

export type BridgeCalling = inferRouterOutputs<AppRouter>["telecom"]["getBridgeCalling"];

const myPhoneOf = (bridgeCalling: BridgeCalling): MyPhone => {
    if (bridgeCalling.ringPhoneNumber) {
        return { kind: "callable", phoneNumber: bridgeCalling.ringPhoneNumber };
    }
    return bridgeCalling.bridgeCallPhoneNumber || bridgeCalling.personalPhoneNumber
        ? { kind: "unusable" }
        : { kind: "missing" };
};

export const effectiveCallFrom = (
    preference: MakeCallsFrom,
    myPhone: MyPhone,
): MakeCallsFrom => (myPhone.kind === "callable" ? preference : "browser");

const callingUsingText = (callFrom: MakeCallsFrom): string => {
    switch (callFrom) {
        case "browser":
            return "Calling using your browser";
        case "phone":
            return "Calling using your phone";
    }
    const exhaustiveCheck: never = callFrom;
    throw new Error(`Unhandled call-from: ${JSON.stringify(exhaustiveCheck)}`);
};

interface CallFromSummaryProps {
    callFrom: MakeCallsFrom;
}

export const CallFromSummary: React.FC<CallFromSummaryProps> = ({ callFrom }) => (
    <div className="flex flex-wrap items-baseline justify-center gap-x-2 text-sm text-gray-500">
        <span>{callingUsingText(callFrom)}</span>
        <InternalLink to={phoneSettingsPath}>Change</InternalLink>
    </div>
);

export type CallFromChoice =
    | { status: "loading" }
    | { status: "error"; callFrom: "browser"; retrying: boolean; retry: () => void }
    | {
          status: "ready";
          callFrom: MakeCallsFrom;
          preference: MakeCallsFrom;
          bridgeCalling: BridgeCalling;
          saveFailed: boolean;
          chooseCallFrom: (callFrom: MakeCallsFrom) => void;
      };

const useSaveMakeCallsFrom = (phoneLineId: TelecomPhoneLineId) => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const bridgeCallingKey = trpcClient.telecom.getBridgeCalling.queryKey({
        phoneLineId,
    });
    return useMutation({
        ...trpcClient.telecom.setMakeCallsFrom.mutationOptions(),
        scope: { id: `setMakeCallsFrom:${phoneLineId}` },
        onMutate: () => queryClient.cancelQueries({ queryKey: bridgeCallingKey }),
        onSuccess: saved => {
            queryClient.setQueryData(bridgeCallingKey, saved);
            handleEvent("make-calls-using-change", {
                phoneLineId,
                callUsing: saved.makeCallsFrom,
            });
        },
    });
};

export const useCallFrom = (phoneLineId: TelecomPhoneLineId): CallFromChoice => {
    const trpcClient = trpc.useTRPC();
    const bridgeCalling = useQuery({
        ...trpcClient.telecom.getBridgeCalling.queryOptions({ phoneLineId }),
        retry: false,
    });
    const save = useSaveMakeCallsFrom(phoneLineId);
    const { mutate: saveCallFrom } = save;

    const chooseCallFrom = useCallback(
        (makeCallsFrom: MakeCallsFrom) => saveCallFrom({ phoneLineId, makeCallsFrom }),
        [phoneLineId, saveCallFrom],
    );
    const retry = useCallback(() => {
        bridgeCalling.refetch().catch((): void => undefined);
    }, [bridgeCalling]);

    if (bridgeCalling.data) {
        const preference =
            save.isPending && save.variables
                ? save.variables.makeCallsFrom
                : bridgeCalling.data.makeCallsFrom;
        return {
            status: "ready",
            callFrom: effectiveCallFrom(preference, myPhoneOf(bridgeCalling.data)),
            preference,
            bridgeCalling: bridgeCalling.data,
            saveFailed: save.isError,
            chooseCallFrom,
        };
    }
    if (bridgeCalling.isError) {
        return {
            status: "error",
            callFrom: "browser",
            retrying: bridgeCalling.isFetching,
            retry,
        };
    }
    return { status: "loading" };
};

interface CallFromChoiceSummaryProps {
    choice: CallFromChoice;
}

export const CallFromChoiceSummary: React.FC<CallFromChoiceSummaryProps> = ({
    choice,
}) => {
    switch (choice.status) {
        case "loading":
            return (
                <div className="flex justify-center">
                    <Spinner />
                </div>
            );
        case "error":
            return (
                <RetryableErrorBanner
                    message="Unable to load your phone number. This call will use your browser."
                    retrying={choice.retrying}
                    onRetry={choice.retry}
                />
            );
        case "ready":
            return <CallFromSummary callFrom={choice.callFrom} />;
    }
    const exhaustiveCheck: never = choice;
    throw new Error(`Unhandled call-from status: ${JSON.stringify(exhaustiveCheck)}`);
};
